import { omit } from 'lodash-es';
import { useMutation, useQueryClient } from 'react-query';
import { VoicemailApi, VoicemailTypes as VmTypes } from '@frontend/api-voicemails';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import {
  performOptimisticDelete,
  performOptimisticUpdate,
  createReadStatusUpdater,
} from '../utils/voicemail-optimistic-updates';

type UseVoicemailMutationsOptions = {
  selectedLocationIds: string[];
  pageSize: number;
  pageNumber: number;
  queryKey: (string | boolean | number)[];
  fetchNextPage?: (options: any) => Promise<any>;
  onDeleteSuccess?: ({ voicemailId, mailboxId }: { voicemailId: string; mailboxId: string }) => void;
};

export const useVoicemailMutations = (options: UseVoicemailMutationsOptions) => {
  const { selectedLocationIds, pageSize, pageNumber, queryKey, fetchNextPage, onDeleteSuccess } = options;
  const { t } = useTranslation('calls');

  const queryClient = useQueryClient();
  const alert = useAlert();

  const fullQueryKey = [selectedLocationIds, ...queryKey] as any[];

  const updateReadStatus = useMutation(
    (payload: VmTypes.StatusPayload) => {
      const req = omit(payload, 'index');
      return VoicemailApi.setVoicemailViewStatus(req);
    },
    {
      onMutate: async ({ voicemailId, status }) => {
        queryClient.cancelQueries([selectedLocationIds, ...queryKey]);

        if (!voicemailId || !status) return;

        const updateFn = createReadStatusUpdater(status);
        const newData = performOptimisticUpdate({
          queryClient,
          fullQueryKey,
          voicemailId,
          pageSize,
          updateFn,
        });

        if (newData) {
          queryClient.setQueryData(fullQueryKey, newData);
        }

        return { oldData: queryClient.getQueryData(fullQueryKey) };
      },
      onError: (_, _1, ctx) => {
        if (ctx?.oldData) {
          queryClient.setQueryData(fullQueryKey, ctx.oldData);
        }
      },
    }
  );

  const deleteVoicemail = useMutation((req: VmTypes.DeletePayload) => VoicemailApi.deleteVoicemailMedia(req), {
    onMutate: async ({ index }) => {
      queryClient.cancelQueries([selectedLocationIds, ...queryKey]);

      const newPageData = await performOptimisticDelete({
        queryClient,
        fullQueryKey,
        pageSize,
        pageNumber,
        index,
        fetchNextPage,
      });

      if (newPageData) {
        queryClient.setQueryData(fullQueryKey, newPageData);
        alert.success(t('voicemailDeletedSuccess'));
      }
    },
    onError: () => {
      alert.error(t('voicemailDeletedError'));
    },
    onSuccess: (_, { voicemailId, mailboxId }) => {
      if (voicemailId) {
        onDeleteSuccess?.({ voicemailId, mailboxId });
      }
    },
  });

  return {
    updateReadStatus: updateReadStatus.mutate,
    deleteVoicemail: deleteVoicemail.mutate,
  };
};
